import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Company } from '@/types/company'
import type { CompanyType } from '@/types/enums'
import {
  createCompany,
  nameDuplicateWarning,
  updateCompany,
  uploadCompanyAsset,
} from '@/api/companies'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Select } from '@/components/ui/Select'
import { Input } from '@/components/ui/Input'
import { formatPhoneInput } from '@/utils/phone'
import {
  digitsOnly,
  validateBankAccount,
  validateBik,
  validateCorrAccount,
  validateInn,
  validateKpp,
  validateOgrn,
} from '@/utils/companyValidators'

const empty: Omit<Company, 'id' | 'created_at' | 'updated_at'> = {
  name: '',
  type: 'customer',
  is_active: true,
  status: 'active',
  deleted_at: null,
  inn: null,
  phone: null,
  legal_address: null,
  email: null,
  k3: 1.0,
  currency: 'RUB',
  signatory_name: null,
  signatory_position: null,
  signatory_basis: null,
  kpp: null,
  ogrn: null,
  bank_account: null,
  bank_name: null,
  bik: null,
  correspondent_account: null,
  facsimile_url: null,
  seal_url: null,
  dkp_template_path: null,
  dkp_template_uploaded_at: null,
  dkp_template_uploaded_by: null,
}

export function CompanyDrawer({
  open,
  onClose,
  onSaved,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onSaved: () => void
  onCreated: (companyId: string) => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState(() => ({ ...empty }))
  // Текстовое значение для number-input «Коэффициент»: храним строкой,
  // чтобы пользователь мог стереть и набрать заново («», «1.», «0.05»).
  const [k3Text, setK3Text] = useState<string>(() => String(empty.k3))
  const [dirty, setDirty] = useState(false)
  const [unsavedConfirmOpen, setUnsavedConfirmOpen] = useState(false)
  const [facsimileFile, setFacsimileFile] = useState<File | null>(null)
  const [sealFile, setSealFile] = useState<File | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const warnDup = form.name.trim() ? nameDuplicateWarning(form.name) : false
  const k3Parsed = Number(k3Text)
  const k3Applicable = form.type === 'customer'
  // Для исполнителя K3 по спеке §6.4 неприменим; валидация поля не нужна,
  // в БД пишем дефолтную 1.0 (колонка NOT NULL).
  const k3Valid =
    !k3Applicable ||
    (k3Text.trim() !== '' && Number.isFinite(k3Parsed) && k3Parsed > 0)

  // Ошибки формата реквизитов (null если валидно/пусто).
  const innError = validateInn(form.inn ?? '')
  const kppError = validateKpp(form.kpp ?? '')
  const ogrnError = validateOgrn(form.ogrn ?? '')
  const bikError = validateBik(form.bik ?? '')
  const bankAccountError = validateBankAccount(form.bank_account ?? '')
  const corrAccountError = validateCorrAccount(form.correspondent_account ?? '')
  const requisitesValid =
    !innError && !kppError && !ogrnError && !bikError &&
    !bankAccountError && !corrAccountError

  /** Все обязательные поля по ТЗ заполнены (для валидной записи в БД). */
  const requiredValid =
    !!form.name?.trim() &&
    !!form.inn?.trim() &&
    !!form.kpp?.trim() &&
    !!form.ogrn?.trim() &&
    !!form.legal_address?.trim() &&
    !!form.bank_account?.trim() &&
    !!form.bank_name?.trim() &&
    !!form.bik?.trim() &&
    !!form.correspondent_account?.trim() &&
    !!form.phone?.trim() &&
    !!form.email?.trim() &&
    !!form.signatory_name?.trim() &&
    !!form.signatory_position?.trim() &&
    !!form.signatory_basis?.trim() &&
    requisitesValid

  async function save() {
    setSubmitError(null)
    if (!k3Valid) return
    if (!requiredValid) {
      setSubmitError(t('companies.requiredAll'))
      return
    }
    const payload = {
      ...form,
      inn: form.inn || null,
      phone: form.phone || null,
      legal_address: form.legal_address || null,
      email: form.email || null,
      k3: k3Applicable ? k3Parsed : 1.0,
    }
    const c = await createCompany(payload)

    // Файлы — приватный bucket company-assets. Грузим уже после insert,
    // когда есть companyId для пути объекта.
    const assetUpdate: { facsimile_url?: string; seal_url?: string } = {}
    if (facsimileFile) {
      assetUpdate.facsimile_url = await uploadCompanyAsset(c.id, 'facsimile', facsimileFile)
    }
    if (sealFile) {
      assetUpdate.seal_url = await uploadCompanyAsset(c.id, 'seal', sealFile)
    }
    if (Object.keys(assetUpdate).length > 0) {
      await updateCompany(c.id, assetUpdate)
    }

    onSaved()
    onCreated(c.id)
    setForm({ ...empty })
    setK3Text(String(empty.k3))
    setFacsimileFile(null)
    setSealFile(null)
    setDirty(false)
    onClose()
  }

  function performClose() {
    setForm({ ...empty })
    setK3Text(String(empty.k3))
    setFacsimileFile(null)
    setSealFile(null)
    setDirty(false)
    setUnsavedConfirmOpen(false)
    onClose()
  }

  function requestClose() {
    if (dirty) {
      setUnsavedConfirmOpen(true)
      return
    }
    performClose()
  }

  if (!open) return null

  return (
    <>
      <ConfirmDialog
        open={unsavedConfirmOpen}
        title={t('companies.unsavedTitle')}
        cancelLabel={t('common.cancel')}
        confirmLabel={t('companies.unsavedClose')}
        confirmVariant="primary"
        onCancel={() => setUnsavedConfirmOpen(false)}
        onConfirm={() => performClose()}
      >
        {t('companies.unsavedBody')}
      </ConfirmDialog>
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[color:var(--stryzh-overlay-scrim-modal)]"
      role="presentation"
      onClick={requestClose}
    >
      <aside
        className="flex h-full w-[var(--stryzh-layout-entity-drawer-width)] max-w-full flex-col overflow-hidden border-l border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] shadow-[var(--stryzh-shadow-glow-strong)]"
        role="dialog"
        aria-labelledby="company-drawer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <h2
            id="company-drawer-title"
            className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]"
          >
            {t('companies.drawerTitle')}
          </h2>
          <Button
            variant="ghost"
            onClick={requestClose}
            aria-label={t('companies.close')}
            className="stryzh-btn--close-icon"
          >
            ✕
          </Button>
        </div>
        <div
          className="flex-1 overflow-y-auto px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]"
          onInput={() => setDirty(true)}
        >
          <div className="space-y-3">
            <Input
              label={t('companies.field.name')}
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            {warnDup ? (
              <p className="font-[family-name:var(--stryzh-font-family-inter)] text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-orange)]">
                {t('companies.dupExistsAllowed')}
              </p>
            ) : null}
            <label className="flex flex-col gap-1">
              <span className="stryzh-input-label">{t('companies.field.type')}</span>
              <Select
                value={form.type}
                onChange={(v) => setForm({ ...form, type: v as CompanyType })}
                options={[
                  { value: 'customer', label: t('companyType.customer') },
                  { value: 'contractor', label: t('companyType.contractor') },
                ]}
              />
            </label>
            <Input
              label={t('companies.field.inn')}
              required
              inputMode="numeric"
              maxLength={12}
              value={form.inn ?? ''}
              onChange={(e) =>
                setForm({ ...form, inn: digitsOnly(e.target.value) || null })
              }
              error={innError ?? undefined}
            />
            <Input
              label={t('companies.field.phone')}
              required
              value={form.phone ?? ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  phone: formatPhoneInput(form.phone ?? '', e.target.value),
                })
              }
            />
            <Input
              label={t('companies.field.legalAddress')}
              required
              value={form.legal_address ?? ''}
              onChange={(e) =>
                setForm({ ...form, legal_address: e.target.value || null })
              }
            />
            <Input
              label={t('companies.field.email')}
              required
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.target.value || null })}
            />
            {form.type === 'customer' ? (
              <Input
                label={t('companies.field.coefficient')}
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                autoComplete="off"
                value={k3Text}
                onChange={(e) => setK3Text(e.target.value)}
                error={k3Valid ? undefined : t('companies.coefficientError')}
                hint={t('companies.coefficientHint')}
              />
            ) : null}
          </div>

          <div className="mt-5 space-y-3 border-t border-[color:var(--stryzh-color-border-default)] pt-4">
            <h3 className="stryzh-input-label">{t('companies.section.requisites')}</h3>
            <div className="grid gap-3 max-[639px]:grid-cols-1 min-[640px]:grid-cols-2">
              <Input
                label={t('companies.field.kpp')} required
                inputMode="numeric" maxLength={9}
                value={form.kpp ?? ''}
                onChange={(e) =>
                  setForm({ ...form, kpp: digitsOnly(e.target.value) || null })
                }
                error={kppError ?? undefined}
              />
              <Input
                label={t('companies.field.ogrn')} required
                inputMode="numeric" maxLength={13}
                value={form.ogrn ?? ''}
                onChange={(e) =>
                  setForm({ ...form, ogrn: digitsOnly(e.target.value) || null })
                }
                error={ogrnError ?? undefined}
              />
              <Input
                label={t('companies.field.bank')} required
                value={form.bank_name ?? ''}
                onChange={(e) =>
                  setForm({ ...form, bank_name: e.target.value || null })
                }
              />
              <Input
                label={t('companies.field.bik')} required
                inputMode="numeric" maxLength={9}
                value={form.bik ?? ''}
                onChange={(e) =>
                  setForm({ ...form, bik: digitsOnly(e.target.value) || null })
                }
                error={bikError ?? undefined}
              />
              <Input
                label={t('companies.field.bankAccount')} required
                inputMode="numeric" maxLength={20}
                value={form.bank_account ?? ''}
                onChange={(e) =>
                  setForm({ ...form, bank_account: digitsOnly(e.target.value) || null })
                }
                error={bankAccountError ?? undefined}
              />
              <Input
                label={t('companies.field.corrAccountFull')} required
                inputMode="numeric" maxLength={20}
                value={form.correspondent_account ?? ''}
                onChange={(e) =>
                  setForm({
                    ...form,
                    correspondent_account: digitsOnly(e.target.value) || null,
                  })
                }
                error={corrAccountError ?? undefined}
              />
            </div>
          </div>

          <div className="mt-5 space-y-3 border-t border-[color:var(--stryzh-color-border-default)] pt-4">
            <h3 className="stryzh-input-label">{t('companies.section.signatory')}</h3>
            <Input
              label={t('companies.field.signatoryName')} required
              value={form.signatory_name ?? ''}
              onChange={(e) =>
                setForm({ ...form, signatory_name: e.target.value || null })
              }
            />
            <Input
              label={t('companies.field.signatoryPosition')} required
              value={form.signatory_position ?? ''}
              onChange={(e) =>
                setForm({ ...form, signatory_position: e.target.value || null })
              }
            />
            <Input
              label={t('companies.field.signatoryBasis')} required
              value={form.signatory_basis ?? ''}
              onChange={(e) =>
                setForm({ ...form, signatory_basis: e.target.value || null })
              }
              hint={t('companies.basisHint')}
            />
          </div>

          <div className="mt-5 space-y-3 border-t border-[color:var(--stryzh-color-border-default)] pt-4">
            <h3 className="stryzh-input-label">{t('companies.section.files')}</h3>
            <label className="flex flex-col gap-1">
              <span className="stryzh-input-label">{t('companies.field.facsimile')}</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFacsimileFile(e.target.files?.[0] ?? null)}
                className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="stryzh-input-label">{t('companies.field.seal')}</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setSealFile(e.target.files?.[0] ?? null)}
                className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)]"
              />
            </label>
          </div>
        </div>
        <div className="border-t border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          {submitError ? (
            <p className="mb-3 text-sm text-[color:var(--stryzh-color-semantic-error)]">
              {submitError}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={requestClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!k3Valid || !requiredValid}
              onClick={() => void save()}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </aside>
    </div>
    </>
  )
}
